import { useEffect } from 'react';
import { Controller, useFieldArray, useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CaretDown, Plus, Trash } from '@phosphor-icons/react';
import * as RadixSwitch from '@radix-ui/react-switch';
import * as Select from '@radix-ui/react-select';
import {
  EditorForm,
  Field,
  inputClasses,
  type EditorSectionProps,
} from './shared';
import { contactSchema, type ContactValues } from '../schemas';
import { PLATFORM_META, PLATFORM_ORDER, detectPlatform } from '../linkPlatforms';

export const CONTACT_SEED: ContactValues = {
  email: '',
  phone: '',
  location: '',
  links: [],
  allowInquiryForm: true,
};

export function ContactEditor({
  initial,
  onDirty,
  onSubmit,
  submitting,
  onDraftChange,
}: EditorSectionProps<ContactValues>) {
  const form = useForm<ContactValues>({
    resolver: zodResolver(contactSchema) as Resolver<ContactValues>,
    defaultValues: initial ?? CONTACT_SEED,
    mode: 'onBlur',
  });
  const { fields, append, remove } = useFieldArray({ control: form.control, name: 'links' });

  useEffect(() => {
    onDraftChange?.(form.getValues());
    const sub = form.watch((v) => {
      onDirty();
      onDraftChange?.(v);
    });
    return () => sub.unsubscribe();
  }, [form, onDirty, onDraftChange]);

  const { errors } = form.formState;

  return (
    <EditorForm onSubmit={form.handleSubmit(async (v) => onSubmit(v))} submitting={submitting}>
      <Field
        label="Public email"
        hint="Shown as a clickable mailto link."
        error={errors.email?.message}
      >
        <input
          {...form.register('email')}
          placeholder="you@example.com"
          className={inputClasses}
        />
      </Field>

      <Field
        label="Phone"
        hint="Optional. Shown as a clickable tel link. Good for CV / resume."
        error={errors.phone?.message}
      >
        <input
          {...form.register('phone')}
          placeholder="+1 555 123 4567"
          inputMode="tel"
          autoComplete="tel"
          className={inputClasses}
        />
      </Field>

      <Field label="Location" hint="Optional. Shown with your contact block." error={errors.location?.message}>
        <input
          {...form.register('location')}
          placeholder="New York, NY"
          className={inputClasses}
        />
      </Field>

      <Field label="Links">
        <div className="space-y-3">
          {fields.map((f, idx) => {
            const platform = form.watch(`links.${idx}.platform`);
            const meta = PLATFORM_META[platform] ?? PLATFORM_META.custom;
            const Icon = meta.icon;
            return (
              <div key={f.id} className="space-y-2 rounded-lg border border-line bg-board p-3">
                <div className="flex items-center gap-2">
                  <Controller
                    control={form.control}
                    name={`links.${idx}.platform`}
                    render={({ field }) => (
                      <Select.Root
                        value={field.value}
                        onValueChange={(next) => {
                          field.onChange(next);
                          if (next !== 'custom') {
                            const currentLabel = form.getValues(`links.${idx}.label`);
                            if (!currentLabel) {
                              form.setValue(`links.${idx}.label`, PLATFORM_META[next as keyof typeof PLATFORM_META].label);
                            }
                          }
                        }}
                      >
                        <Select.Trigger
                          className="inline-flex min-w-[150px] items-center justify-between gap-2 rounded-md border border-line bg-board px-3 py-2 text-sm text-ink focus:border-ink focus:outline-none"
                          aria-label="Platform"
                        >
                          <span className="inline-flex items-center gap-2">
                            <Icon size={14} className="text-ink-muted" />
                            <Select.Value />
                          </span>
                          <Select.Icon>
                            <CaretDown size={12} className="text-ink-faint" />
                          </Select.Icon>
                        </Select.Trigger>
                        <Select.Portal>
                          <Select.Content
                            position="popper"
                            sideOffset={4}
                            className="z-50 max-h-64 overflow-hidden rounded-lg border border-line bg-board shadow-float"
                          >
                            <Select.Viewport className="p-1">
                              {PLATFORM_ORDER.map((p) => {
                                const m = PLATFORM_META[p];
                                const I = m.icon;
                                return (
                                  <Select.Item
                                    key={p}
                                    value={p}
                                    className="flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-1.5 text-sm text-ink outline-none data-[highlighted]:bg-rail"
                                  >
                                    <I size={14} className="text-ink-muted" />
                                    <Select.ItemText>{m.label}</Select.ItemText>
                                  </Select.Item>
                                );
                              })}
                            </Select.Viewport>
                          </Select.Content>
                        </Select.Portal>
                      </Select.Root>
                    )}
                  />
                  <input
                    {...form.register(`links.${idx}.label`)}
                    placeholder="Label"
                    className={`${inputClasses} flex-1`}
                  />
                  <button
                    type="button"
                    onClick={() => remove(idx)}
                    aria-label="Remove link"
                    className="rounded-md border border-line bg-board p-2 text-ink-muted hover:border-red-300 hover:text-red-700"
                  >
                    <Trash size={14} />
                  </button>
                </div>
                <div>
                  <input
                    {...form.register(`links.${idx}.href`, {
                      onBlur: (e) => {
                        const next = e.target.value.trim();
                        if (!next) return;
                        const currentPlatform = form.getValues(`links.${idx}.platform`);
                        if (currentPlatform === 'custom') {
                          const detected = detectPlatform(next);
                          if (detected !== 'custom') {
                            form.setValue(`links.${idx}.platform`, detected);
                            if (!form.getValues(`links.${idx}.label`)) {
                              form.setValue(`links.${idx}.label`, PLATFORM_META[detected].label);
                            }
                          }
                        }
                      },
                    })}
                    placeholder={meta.placeholder}
                    className={inputClasses}
                  />
                  {errors.links?.[idx]?.href?.message ? (
                    <p className="mt-1 text-[11px] text-red-600">
                      {errors.links[idx]?.href?.message}
                    </p>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
        <button
          type="button"
          onClick={() =>
            append({
              id: Math.random().toString(36).slice(2, 8),
              platform: 'custom',
              label: '',
              href: '',
            })
          }
          className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-ink hover:underline"
        >
          <Plus size={12} /> Add link
        </button>
      </Field>

      <Field label="Inquiry form">
        <Controller
          control={form.control}
          name="allowInquiryForm"
          render={({ field }) => (
            <div className="flex items-start gap-3 rounded-lg border border-line bg-board p-4">
              <RadixSwitch.Root
                checked={field.value}
                onCheckedChange={field.onChange}
                className="relative mt-0.5 h-6 w-11 shrink-0 rounded-full border border-line bg-rail transition-colors data-[state=checked]:border-ink data-[state=checked]:bg-ink"
              >
                <RadixSwitch.Thumb className="absolute left-0 top-1/2 block h-[18px] w-[18px] -translate-y-1/2 translate-x-[2px] rounded-full bg-board shadow-sm transition-transform duration-150 data-[state=checked]:translate-x-[22px]" />
              </RadixSwitch.Root>
              <div>
                <p className="text-sm font-medium text-ink">
                  Allow visitors to send an inquiry
                </p>
                <p className="mt-0.5 text-xs text-ink-muted">
                  Adds a short form. Messages route to your email and Inquiries tab.
                </p>
              </div>
            </div>
          )}
        />
      </Field>
    </EditorForm>
  );
}
